import React, { useRef, useState, useEffect } from "react";
import {
  Button, TextField, CircularProgress, Typography,
  Link, Box, Alert, InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { requestPasswordReset } from "../../services/AuthentificationService";
import { motion, AnimatePresence } from "framer-motion";
import AuthLayout, { itemVariants } from "./AuthLayout";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.88)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.96)",
      "& fieldset": { borderColor: "rgba(255,255,255,0.8)" },
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 4px rgba(255,255,255,0.16), 0 12px 26px rgba(2,29,71,0.18)",
      "& fieldset": { borderColor: "#03407b", borderWidth: 2 },
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#03407b" },
}));

const SendButton = styled(Button)(() => ({
  borderRadius: 12,
  padding: "13px 0",
  fontSize: "1rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
  background: "linear-gradient(135deg, #03407b 0%, #02357a 100%)",
  boxShadow: "0 4px 15px rgba(99,102,241,0.4)",
  transition: "all 0.25s ease",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #02357a 0%, #022d68 100%)",
    boxShadow: "0 6px 20px rgba(99,102,241,0.5)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#e2e8f0", boxShadow: "none", color: "#94a3b8" },
}));

const BrandContent = () => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
    <img
      src={process.env.REACT_APP_LOGO_URL}
      alt="Logo"
      style={{ width: 174, filter: "brightness(0) invert(1)", marginBottom: 22 }}
    />
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box sx={{
        width: 70, height: 70, borderRadius: "22px",
        background: "rgba(255,255,255,0.18)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.3)",
        display: "flex", alignItems: "center", justifyContent: "center",
        mx: "auto", mb: 2.5,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
      }}>
        <LockResetOutlinedIcon sx={{ fontSize: 36, color: "#fff" }} />
      </Box>
    </motion.div>

    <Typography variant="h3" sx={{ color: "#ffffff", fontWeight: 900, lineHeight: 1.1, fontSize: { xs: "1.55rem", md: "2rem" }, textShadow: "0 14px 30px rgba(2, 29, 71, 0.35)", mb: 1.2 }}>
      Récupérez votre accès
    </Typography>
    <Typography sx={{ color: "#dff4ff", fontSize: "1rem", fontWeight: 600, lineHeight: 1.5, maxWidth: 400, mx: "auto", textShadow: "0 8px 18px rgba(2, 29, 71, 0.22)" }}>
      Entrez votre adresse email et nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe.
    </Typography>
  </Box>
);

const SuccessView = ({ email }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    <Box sx={{ textAlign: "center" }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: "20px",
        background: "linear-gradient(135deg, #10b981, #059669)",
        display: "flex", alignItems: "center", justifyContent: "center",
        mx: "auto", mb: 3,
        boxShadow: "0 8px 24px rgba(16,185,129,0.35)",
      }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#fff" }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 800, color: "#ffffff", mb: 1, textShadow: "0 8px 18px rgba(2, 29, 71, 0.22)" }}>
        Email envoyé !
      </Typography>
      <Typography sx={{ color: "#cdeeff", fontSize: "1rem", mb: 1, fontWeight: 600 }}>
        Un lien de réinitialisation a été envoyé à
      </Typography>
      <Typography sx={{
        color: "#ffffff", fontWeight: 700, fontSize: "1rem",
        background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", borderRadius: 2, px: 2, py: 0.75,
        display: "inline-block", mb: 3,
      }}>
        {email}
      </Typography>
      <Typography sx={{ color: "#d9f2ff", fontSize: "1rem", mb: 4, fontWeight: 600 }}>
        Vérifiez votre boîte de réception et vos spams. Le lien expire dans 30 minutes.
      </Typography>

      <Link
        component={RouterLink}
        to="/login"
        sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75,
          color: "#e3f7ff", fontWeight: 750, fontSize: "1rem",
          textDecoration: "none",
          textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)",
          "&:hover": { color: "#ffffff", textDecoration: "underline" },
        }}
      >
        <ArrowBackIcon fontSize="small" />
        Retour à la connexion
      </Link>
    </Box>
  </motion.div>
);

export default function ForgotPassword({ snackbarShowMessage }) {
  const mounted = useRef(true);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [sent, setSent] = useState(false);

  useEffect(() => () => { mounted.current = false; }, []);

  const validate = () => {
    const temp = { email: email ? "" : "L'adresse email est obligatoire." };
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setApiError("");
    if (!validate()) return;

    setLoading(true);
    try {
      await requestPasswordReset({ email });
      setSent(true);
      snackbarShowMessage(
        "Si cet email existe, un lien de réinitialisation a été envoyé.",
        "success", 5000
      );
    } catch (err) {
      const msg = err.response?.data?.message || "Une erreur s'est produite. Réessayez plus tard.";
      setApiError(msg);
      snackbarShowMessage(msg, "error", 5000);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  return (
    <AuthLayout brandContent={<BrandContent />}>
      <AnimatePresence mode="wait">
        {sent ? (
          <SuccessView key="success" email={email} />
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            
            <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible">
              <Typography variant="h4" sx={{ fontWeight: 850, color: "#ffffff", mb: 0.75, fontSize: "1.45rem", textAlign: "center", textShadow: "0 8px 18px rgba(2, 29, 71, 0.22)" }}>
                Mot de passe oublié ?
              </Typography>
              <Typography sx={{ color: "#cdeeff", fontSize: "1rem", mb: 4, textAlign: "center", fontWeight: 500 }}>
                Saisissez votre email pour recevoir un lien de réinitialisation.
              </Typography>
            </motion.div>

            
            <AnimatePresence>
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setApiError("")}>
                    {apiError}
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            
            <Box
              component="form"
              noValidate
              onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              
              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible">
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: "#f4fbff", mb: 0.75, letterSpacing: "0.01em", textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)" }}>
                    ADRESSE EMAIL
                  </Typography>
                  <StyledTextField
                    variant="outlined"
                    fullWidth
                    placeholder="vous@exemple.com"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
                    }}
                    error={!!errors.email}
                    helperText={errors.email} slotProps={{ input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    } }}
                  />
                </Box>
              </motion.div>

              
              <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible">
                <SendButton
                  type="button"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CircularProgress size={18} sx={{ color: "#fff" }} />
                      <span>Envoi en cours…</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MarkEmailReadOutlinedIcon fontSize="small" />
                      <span>Envoyer le lien</span>
                    </Box>
                  )}
                </SendButton>
              </motion.div>

              
              <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible">
                <Box sx={{ textAlign: "center", mt: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.5,
                      color: "#e3f7ff", fontWeight: 750, fontSize: "1rem",
                      textDecoration: "none",
                      textShadow: "0 6px 14px rgba(2, 29, 71, 0.25)",
                      "&:hover": { color: "#ffffff", textDecoration: "underline" },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                    Retour à la connexion
                  </Link>
                </Box>
              </motion.div>
            </Box>

            
            <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible">
              <Typography sx={{ mt: 5, textAlign: "center", fontSize: "1rem", color: "#d9f2ff", fontWeight: 600 }}>
                © {new Date().getFullYear()} Vigor CRM · Tous droits réservés
              </Typography>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
